import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { XCircle } from "phosphor-react";
import ReactInputMask from "react-input-mask";
import { yupResolver } from "@hookform/resolvers/yup";

import { Input } from "../../../../../components/FormElements/Input";
import { Button } from "../../../../../components/Button";
import { useAuth } from "../../../../../contexts/AuthContext";
import { type LegalPersonPersistence } from "../../../../../types/persistence/legal-person-persistence";
import { legalPersonSchema } from "../../../../../validators/legalPersonSchema";
import { useExpertLegalDataMutation } from "../../../../../services/hooks/experts/mutations/useExpertLegalDataMutation";
import { useExpertQuery } from "../../../../../services/hooks/experts/queries/useExpertQuery";
import { isEmailAvailable } from "../../../../../utils/isEmailAvailable";
import { formatDocument } from "../../../../../utils/document";
import { isAxiosError } from "axios";
import { getErrorMessage } from "../../../../../utils/getErrorMessage";
import { TOAST_ERROR_DEFAULT_OPTIONS } from "../../../../../constants/toast";
import { api } from "../../../../../services/api";
import { isCpfAvailable } from "../../../../../utils/isCpfAlreadyInUse";
import { normalizeCpf } from "../../../../../utils/normalizeCpf";
import register2 from "../../../../../assets/images/register-2.png";

type LegalPersonProps = {
  onGoToNextStep: () => void;
  onGoPreviousStep: () => void;
};

export const LegalPerson = ({
  onGoToNextStep,
  onGoPreviousStep,
}: LegalPersonProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data } = useExpertQuery(user?.id!);
  const { mutate, isLoading } = useExpertLegalDataMutation();
  const { formatMessage } = useIntl();

  const { control, handleSubmit, setValue, clearErrors, setError, formState } =
    useForm<LegalPersonPersistence>({
      resolver: yupResolver(legalPersonSchema),
      mode: "onChange",
      defaultValues: {
        name: data?.name,
        email: data?.email,
        phone: data?.phone,
        cnpj: data?.legalData?.cnpj,
        companyName: data?.legalData?.companyName,
        cpf: data?.cpf?.replace(/^(\d{3})(\d{3})(\d{3})(\d)/, "$1.$2.$3-$4"),
        birthdate: data?.naturalData?.birthdate,
        rg: data?.naturalData?.rg,
        linkedin: data?.linkedin || "",
        cep: data?.naturalData?.cep,
        street: data?.naturalData?.street,
        addressNumber: data?.naturalData?.addressNumber,
        complement: data?.naturalData?.complement || "",
        neighborhood: data?.naturalData?.neighborhood,
        city: data?.naturalData?.city,
        state: data?.naturalData?.state,
      },
    });

  const validateCpf = async (cpf: string) => {
    try {
      const formattedCpfFieldValue = formatDocument(cpf);

      if (formattedCpfFieldValue) {
        const { data } = await api.get(
          `/experts/validate-cpf/${formattedCpfFieldValue}`
        );

        setValue("name", data.name);
        setValue("name", data.name);
        setValue("birthdate", data.birthdate);
        clearErrors("cpf");
      }
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.data.message === "CPF not found") {
          setError("cpf", { message: "INVALID_CPF" });
          return;
        }
      }

      toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS);
    }
  };

  const validateCep = async (cep: string) => {
    try {
      const formattedCepFieldValue = formatDocument(cep);

      if (formattedCepFieldValue) {
        const { data } = await api.get(
          `https://viacep.com.br/ws/${formattedCepFieldValue}/json/`
        );

        if (!data.erro) {
          setValue("street", data.logradouro);
          setValue("city", data.localidade);
          setValue("state", data.uf);
          setValue("neighborhood", data.bairro);
          clearErrors("cep");
          return;
        }

        setValue("city", "");
        setValue("state", "");
        setValue("neighborhood", "");
        setValue("street", "");
        setError("cep", { message: "INVALID_CEP" });
      }
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS);
    }
  };

  const onSubmitLegalData = (info: LegalPersonPersistence) => {
    if (formState.isValid && data?.id) {
      mutate(
        { id: data?.id, data: { ...info, cpf: normalizeCpf(info.cpf) } },
        {
          onSuccess() {
            onGoToNextStep();
          },
          onError(error: any) {
            let message: string;

            if (error.response?.data?.statusCode === 400) {
              message = formatMessage({
                id: "CPF_EXISTS",
              });
            } else {
              message = error.response?.data?.message;
            }

            toast(message, {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              closeButton: false,
              icon: (
                <XCircle
                  size={48}
                  className="mx-auto my-auto text-alert-error"
                />
              ),
              className: "bg-red-50",
            });
          },
        }
      );
    }
  };

  return (
    <section className="md:py-5">
      <div className="container mx-auto">
        <div className="flex justify-center">
          <div className="hidden md:block w-1/4">
            <img src={register2} alt="Text with image" />
          </div>
          <div className="w-full md:w-3/4 md:ps-8">
            <form
              onSubmit={handleSubmit(onSubmitLegalData)}
              className="flex flex-col w-full h-full lg:max-w-[90%]"
            >
              <header className="mb-10">
                <h2 className="mb-6 text-2xl font-semibold">
                  {formatMessage({ id: "FINISH_YOUR_REGISTRATION_TITLE" })}
                </h2>
                <p className="">
                  {formatMessage({
                    id: "FINISH_YOUR_REGISTRATION_SUBTITLE",
                  })}
                </p>
              </header>

              <div className="w-full flex flex-col gap-[25px]">
                <div className="flex flex-wrap gap-5">
                  <div className="w-[calc(50%-10px)]">
                    <Controller
                      name="companyName"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { invalid, error, isDirty },
                      }) => (
                        <Input
                          label={formatMessage({ id: "CORPORATE_NAME" })}
                          placeholder={formatMessage({ id: "CORPORATE_NAME" })}
                          isValid={!invalid && isDirty}
                          value={value}
                          onChange={onChange}
                          errorMessage={
                            error?.message &&
                            formatMessage({ id: error.message })
                          }
                        />
                      )}
                    />
                  </div>
                  <div className="w-[calc(50%-10px)] md:w-[calc(30%-10px)]">
                    <Controller
                      name="cnpj"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { invalid, error, isDirty },
                      }) => (
                        <ReactInputMask
                          mask="99.999.999/9999-99"
                          value={value}
                          onChange={onChange}
                          maskPlaceholder={null}
                        >
                          <Input
                            label="CNPJ"
                            placeholder="00.000.000/0000-00"
                            isValid={!invalid && isDirty}
                            errorMessage={
                              error?.message &&
                              formatMessage({ id: error.message })
                            }
                          />
                        </ReactInputMask>
                      )}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-5">
                  <div className="w-full md:w-[calc(50%-10px)]">
                    <Controller
                      name="name"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { invalid, error, isDirty },
                      }) => (
                        <Input
                          label={formatMessage({ id: "FULL_NAME" })}
                          placeholder={formatMessage({
                            id: "NAME_AND_SURNAME",
                          })}
                          isValid={!invalid && isDirty}
                          value={value}
                          onChange={onChange}
                          errorMessage={
                            error?.message &&
                            formatMessage({ id: error.message })
                          }
                        />
                      )}
                    />
                  </div>
                  <div className="w-full md:w-[calc(50%-10px)]">
                    <Controller
                      name="email"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { invalid, error, isDirty },
                      }) => (
                        <Input
                          label="E-mail"
                          placeholder="exemple@email.com"
                          isValid={!invalid && isDirty}
                          value={value}
                          onChange={async (event) => {
                            onChange(event);

                            if (
                              !(await isEmailAvailable(
                                event.target.value,
                                user!.email
                              ))
                            ) {
                              setError("email", {
                                message: "EMAIL_ALREADY_IN_USE",
                              });
                              return;
                            }

                            clearErrors("email");
                          }}
                          errorMessage={
                            error?.message &&
                            formatMessage({ id: error.message })
                          }
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-5">
                  <div className="w-[calc(50%-10px)] md:w-1/5">
                    <Controller
                      name="rg"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { invalid, error, isDirty },
                      }) => (
                        <ReactInputMask
                          mask=""
                          maskPlaceholder={null}
                          value={value}
                          onChange={onChange}
                        >
                          <Input
                            label="RG"
                            placeholder="00.000.000-0"
                            isValid={!invalid && isDirty}
                            errorMessage={
                              error?.message &&
                              formatMessage({ id: error.message })
                            }
                          />
                        </ReactInputMask>
                      )}
                    />
                  </div>
                  <div className="w-[calc(50%-10px)] md:w-1/4">
                    <Controller
                      name="cpf"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { invalid, error, isDirty },
                      }) => (
                        <ReactInputMask
                          mask="999.999.999-99"
                          maskPlaceholder={null}
                          value={value || ""}
                          onChange={async (event) => {
                            onChange(event);

                            const currentValue = event.target.value;
                            const cpfPattern = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;

                            if (cpfPattern.test(currentValue)) {
                              if (
                                await isCpfAvailable(currentValue, user?.cpf)
                              ) {
                                clearErrors("cpf");
                                validateCpf(currentValue);
                                return;
                              }

                              setError("cpf", { message: "CPF_EXISTS" });
                            }
                          }}
                        >
                          <Input
                            label="CPF"
                            placeholder="000.000.000-00"
                            isValid={!invalid && isDirty}
                            errorMessage={
                              error?.message &&
                              formatMessage({ id: error.message })
                            }
                          />
                        </ReactInputMask>
                      )}
                    />
                  </div>
                  <div className="w-[calc(50%-10px)] md:w-1/4">
                    <Controller
                      name="birthdate"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { invalid, error, isDirty },
                      }) => (
                        <ReactInputMask
                          mask="99/99/9999"
                          maskPlaceholder={null}
                          value={value}
                          onChange={onChange}
                        >
                          <Input
                            label={formatMessage({ id: "DATE_OF_BIRTH" })}
                            placeholder="00/00/0000"
                            isValid={!invalid && isDirty}
                            errorMessage={
                              error?.message &&
                              formatMessage({ id: error.message })
                            }
                          />
                        </ReactInputMask>
                      )}
                    />
                  </div>
                </div>
                <div className="flex gap-5">
                  <div className="w-1/2">
                    <Controller
                      name="linkedin"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { invalid, error, isDirty },
                      }) => (
                        <Input
                          label="LinkedIn"
                          placeholder="https://www.linkedin.com/in/example-user-name"
                          isValid={!invalid && isDirty}
                          value={value}
                          onChange={onChange}
                          errorMessage={
                            error?.message &&
                            formatMessage({ id: error.message })
                          }
                        />
                      )}
                    />
                  </div>
                  <div className="w-1/2 md:w-1/4">
                    <Controller
                      name="phone"
                      control={control}
                      render={({
                        field: { value = "", onChange },
                        fieldState: { invalid, error, isDirty },
                      }) => (
                        <ReactInputMask
                          mask="+99 (99) 99999-9999"
                          value={value}
                          onChange={onChange}
                        >
                          <Input
                            label={formatMessage({ id: "TELEPHONE" })}
                            placeholder="(00) 00000-0000"
                            isValid={!invalid && isDirty}
                            errorMessage={
                              error?.message &&
                              formatMessage({ id: error.message })
                            }
                          />
                        </ReactInputMask>
                      )}
                    />
                  </div>
                </div>

                <div className="p-5 pb-6 mt-4 border border-gray-400 rounded-[36px] rounded-tl-none">
                  <label className="flex items-center gap-2 ps-5 pe-8 -mt-9 mb-5 -ms-5 w-fit font-medium bg-white">
                    {formatMessage({ id: "ADDRESS" })}
                  </label>
                  <div className="w-full flex flex-col gap-[25px]">
                    <div className="flex flex-wrap gap-5">
                      <div className="w-[calc(50%-10px)]">
                        <Controller
                          name="street"
                          control={control}
                          render={({
                            field: { value = "", onChange },
                            fieldState: { invalid, error, isDirty },
                          }) => (
                            <Input
                              label={formatMessage({ id: "STREET" })}
                              placeholder={formatMessage({ id: "STREET" })}
                              isValid={!invalid && isDirty}
                              value={value}
                              onChange={onChange}
                              errorMessage={
                                error?.message &&
                                formatMessage({ id: error.message })
                              }
                            />
                          )}
                        />
                      </div>
                      <div className="w-[calc(50%-10px)]">
                        <Controller
                          name="neighborhood"
                          control={control}
                          render={({
                            field: { value = "", onChange },
                            fieldState: { invalid, error, isDirty },
                          }) => (
                            <Input
                              label={formatMessage({ id: "NEIGHBORHOOD" })}
                              placeholder={formatMessage({
                                id: "NEIGHBORHOOD",
                              })}
                              isValid={!invalid && isDirty}
                              value={value}
                              onChange={onChange}
                              errorMessage={
                                error?.message &&
                                formatMessage({ id: error.message })
                              }
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-5">
                      <div className="w-[calc(50%-10px)] md:w-1/5">
                        <Controller
                          name="addressNumber"
                          control={control}
                          render={({
                            field: { value = "", onChange },
                            fieldState: { invalid, error, isDirty },
                          }) => (
                            <Input
                              label={formatMessage({ id: "NUMBER" })}
                              placeholder="000"
                              isValid={!invalid && isDirty}
                              value={value}
                              onChange={onChange}
                              errorMessage={
                                error?.message &&
                                formatMessage({ id: error.message })
                              }
                            />
                          )}
                        />
                      </div>
                      <div className="w-[calc(50%-10px)] md:w-1/4">
                        <Controller
                          name="complement"
                          control={control}
                          render={({
                            field: { value = "", onChange },
                            fieldState: { invalid, error, isDirty },
                          }) => (
                            <Input
                              label={formatMessage({ id: "COMPLEMENT" })}
                              placeholder={formatMessage({ id: "COMPLEMENT" })}
                              isValid={!invalid && isDirty}
                              value={value || ""}
                              onChange={onChange}
                              errorMessage={
                                error?.message &&
                                formatMessage({ id: error.message })
                              }
                            />
                          )}
                        />
                      </div>
                      <div className="w-[calc(50%-10px)] md:w-1/4">
                        <Controller
                          name="cep"
                          control={control}
                          render={({
                            field: { value = "", onChange },
                            fieldState: { invalid, error, isDirty },
                          }) => (
                            <ReactInputMask
                              mask="99999-999"
                              maskPlaceholder={null}
                              value={value}
                              onChange={(event) => {
                                onChange(event);

                                const currentValue = event.target.value;
                                const cepPattern = /^\d{5}-\d{3}$/;

                                if (cepPattern.test(currentValue)) {
                                  validateCep(currentValue);
                                }
                              }}
                            >
                              <Input
                                label="CEP"
                                placeholder="00000-000"
                                isValid={!invalid && isDirty}
                                errorMessage={
                                  error?.message &&
                                  formatMessage({ id: error?.message })
                                }
                              />
                            </ReactInputMask>
                          )}
                        />
                      </div>
                    </div>
                    <div className="flex gap-5">
                      <div className="w-1/2 md:w-2/6">
                        <Controller
                          name="city"
                          control={control}
                          render={({
                            field: { value = "", onChange },
                            fieldState: { invalid, error, isDirty },
                          }) => (
                            <Input
                              label={formatMessage({ id: "CITY" })}
                              placeholder={formatMessage({ id: "CITY" })}
                              isValid={!invalid && isDirty}
                              value={value}
                              onChange={onChange}
                              errorMessage={
                                error?.message &&
                                formatMessage({ id: error.message })
                              }
                            />
                          )}
                        />
                      </div>
                      <div className="w-1/2 md:w-2/6">
                        <Controller
                          name="state"
                          control={control}
                          render={({
                            field: { value = "", onChange },
                            fieldState: { invalid, error, isDirty },
                          }) => (
                            <Input
                              label={formatMessage({ id: "STATE" })}
                              placeholder={formatMessage({ id: "STATE" })}
                              isValid={!invalid && isDirty}
                              value={value}
                              onChange={onChange}
                              errorMessage={
                                error?.message &&
                                formatMessage({ id: error.message })
                              }
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <footer className="w-full mt-14 flex flex-col md:flex-row justify-center items-center gap-4">
                <Button
                  onClick={onGoPreviousStep}
                  variant="outline"
                  borderColor="border-primary-light"
                  size="large"
                  className="ps-12 pe-12"
                >
                  {formatMessage({ id: "BACK" })}
                </Button>
                <Button
                  disabled={
                    !formState.isValid ||
                    Object.entries(formState.errors).length > 0
                  }
                  isLoading={isLoading}
                  type="submit"
                  size="large"
                  className="ps-12 pe-12"
                >
                  {formatMessage({ id: "CONTINUE" })}
                </Button>
              </footer>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
